import Select, { type SelectOption } from '@rio-cloud/rio-uikit/Select';
import isEmpty from 'lodash/fp/isEmpty';
import isUndefined from 'lodash/fp/isUndefined';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { useRouteWidgetContext } from '../../context/RouteWidgetContext';
import { loadProfiles } from '../../features/fetchData/loadProfiles';
import {
    type PermittedAsset,
    PlugType,
    Status,
    useGetPermittedAssetsQuery,
    useGetVehicleProfileDetailsQuery,
    useGetVehicleProfileListQuery,
    useLazyGetAssetDataQuery,
} from '../../store/facade/facadeApiV3';
import {
    selectIsEV,
    selectProfileType,
    selectVehicleProfile,
} from '../../store/vehicleProfile/vehicleProfileSelectors';
import {
    changeAssetLocked,
    changeBatteryCapacity,
    changeBatteryPacksValue,
    changeIsEV,
    changePlugType,
    changeProfileDetails,
    clearVehicleProfileData,
    lockVehicleType,
    unlockVehicleType,
} from '../../store/vehicleProfile/vehicleProfileSlice';
import { VehicleType } from '../../store/vehicleProfile/vehicleProfileTypes';
import { sendMessage } from '../../store/widget/widgetMessaging';
import { widgetActions } from '../../store/widget/widgetSlice';

export const VehicleAutoSuggest = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const widgetContext = useRouteWidgetContext();

    const assetLocked = useAppSelector(selectVehicleProfile).assetLocked;
    const assetLockedId = assetLocked?.asset_id;

    const isEV = useAppSelector(selectIsEV).value;
    const selectedProfile = useAppSelector(selectProfileType);
    const { data: profileDetails } = useGetVehicleProfileDetailsQuery(
        { id: selectedProfile as string },
        { skip: isUndefined(selectedProfile) || !isEV || selectedProfile === VehicleType.CUSTOM_EV }
    );

    const [suggestions, setSuggestions] = useState<SelectOption[]>([]);
    const [activeAsset, setActiveAsset] = useState<PermittedAsset[]>([]);
    const [inactiveAssets, setInactiveAssets] = useState<PermittedAsset[]>([]);
    const [fetchVehicleData] = useLazyGetAssetDataQuery();

    const { data: fetchedVehicles } = useGetPermittedAssetsQuery();
    const { data: profilesList } = useGetVehicleProfileListQuery();

    useEffect(() => {
        if (!assetLocked || !profilesList) {
            return;
        }
        const vehicleProfileId = assetLocked.vehicleProfileId;
        if (vehicleProfileId && profilesList.some(profile => profile.id === vehicleProfileId)) {
            dispatch(lockVehicleType(vehicleProfileId));
        } else {
            // Unlock profile if the new asset has no matching vehicleProfileId
            dispatch(unlockVehicleType());
        }
    }, [assetLocked, profilesList, dispatch]);

    useEffect(() => {
        // Only auto-select if no vehicle is currently selected and we have suggestions
        const targetIds = [assetLockedId, widgetContext.assetSelected].filter(Boolean);
        if (!isEmpty(targetIds) && !isEmpty(suggestions) && !assetLocked) {
            const option = suggestions.find(suggestion => targetIds.includes(suggestion.id));
            if (option) {
                handleSuggestionSelected(option);
            }
        }
    }, [suggestions]);

    useEffect(() => {
        if (fetchedVehicles) {
            const vehiclesFormatted: SelectOption[] = [];

            vehiclesFormatted.push({
                id: '1',
                label: intl.formatMessage({
                    id: 'intl-msg:smartRoute.generic.subscribed',
                }),
                header: true,
            });

            const activeVehicles: PermittedAsset[] = fetchedVehicles.items.filter(
                v => v.subscription_state?.status === Status.Active
            );
            setActiveAsset(activeVehicles);

            vehiclesFormatted.push(...activeVehicles.map(it => ({ label: it.name, id: it.id })));

            vehiclesFormatted.push({
                id: '2',
                label: intl.formatMessage({
                    id: 'intl-msg:smartRoute.generic.unsubscribed',
                }),
                header: true,
            });

            const inactiveAssets: PermittedAsset[] = fetchedVehicles.items.filter(
                v => v.subscription_state?.status !== Status.Active
            );
            setInactiveAssets(inactiveAssets);

            vehiclesFormatted.push(...inactiveAssets.map(it => ({ label: it.name, id: it.id })));

            setSuggestions(vehiclesFormatted);
        }
    }, [fetchedVehicles]);

    const handleSuggestionSelected = async (selectedSuggestion: SelectOption | undefined) => {
        if (isUndefined(selectedSuggestion)) {
            dispatch(clearVehicleProfileData());
            dispatch(loadProfiles);
            dispatch(changeAssetLocked(undefined));
            widgetContext.setAssetSelected(undefined);
            widgetContext.setIsAssetNotBooked(false);
            return;
        }

        const assetId = selectedSuggestion.id;
        if (isEmpty(assetId)) {
            return;
        }
        widgetContext.setAssetSelected(assetId);
        dispatch(changeIsEV(assetLocked?.details?.details_bev ?? true));

        const isActiveAsset = activeAsset.some(item => item.id === assetId);
        if (isActiveAsset) {
            const vehicle = await fetchVehicleData({ id: assetId }, true).unwrap();
            if (!isUndefined(vehicle?.asset_id)) {
                const { asset_id, batteryConfiguration } = vehicle;
                const batteryPacksNumber = batteryConfiguration?.batteryPacksNumber;
                dispatch(changeAssetLocked(vehicle));
                if (batteryPacksNumber && batteryPacksNumber !== 0) {
                    dispatch(
                        changeBatteryCapacity({
                            value: batteryPacksNumber * (profileDetails?.batteryCapacityPerPack ?? 0),
                            isEditable: false,
                        })
                    );
                    dispatch(changeBatteryPacksValue(batteryPacksNumber));
                } else if (profileDetails) {
                    dispatch(changeProfileDetails(profileDetails));
                }

                const chargePortType = batteryConfiguration?.chargePortType;
                if (chargePortType && chargePortType.length > 0) {
                    const port = chargePortType[0].toUpperCase();
                    if (port === 'MCS') {
                        dispatch(changePlugType({ value: PlugType.Mcs, isEditable: false }));
                    } else {
                        dispatch(changePlugType({ value: PlugType.CcsCombo2, isEditable: false }));
                    }
                }

                const { vehicleProfileId } = vehicle;
                if (vehicleProfileId && profilesList?.some(profile => profile.id === vehicleProfileId)) {
                    dispatch(lockVehicleType(vehicleProfileId));
                }

                if (asset_id !== widgetContext.assetSelected) {
                    sendMessage(widgetActions.selectAsset({ id: asset_id }));
                }
            }
            widgetContext.setIsAssetNotBooked(false);
        } else {
            const isInactiveAsset = inactiveAssets.some(item => item.id === assetId);
            widgetContext.setIsAssetNotBooked(isInactiveAsset);
        }
    };

    return (
        <Select
            id='vehicle-auto-suggest'
            placeholder={intl.formatMessage({ id: 'intl-msg:smartRoute.vehicleSelect.placeholder' })}
            noItemMessage={intl.formatMessage({ id: 'intl-msg:smartRoute.vehicleSelect.noItems' })}
            onChange={handleSuggestionSelected}
            value={assetLockedId ? [assetLockedId] : []}
            options={suggestions}
            useFilter
            useClear
        />
    );
};
